import type { AiSettings } from '../types';

interface Props {
  form: AiSettings;
  set: <K extends keyof AiSettings>(key: K, val: AiSettings[K]) => void;
}

const MAX = 1000;

interface ContextFieldProps {
  label: string;
  icon: string;
  fieldKey: keyof AiSettings;
  value: string;
  placeholder: string;
  hint: string;
  rows?: number;
  onChange: (val: string) => void;
}

function ContextField({
  label,
  icon,
  value,
  placeholder,
  hint,
  rows = 4,
  onChange,
}: ContextFieldProps) {
  const len = value.length;
  const nearLimit = len > MAX * 0.85;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <h3 className="text-[#0f172a] font-semibold text-sm flex items-center gap-2">
        <span className="w-6 h-6 flex items-center justify-center">
          <i className={`${icon} text-[#C8A96E]`} />
        </span>
        {label}
      </h3>
      <textarea
        value={value}
        onChange={(e) => {
          if (e.target.value.length <= MAX) onChange(e.target.value);
        }}
        placeholder={placeholder}
        rows={rows}
        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all resize-none"
      />
      <div className="flex items-start justify-between gap-3">
        <p className="text-slate-400 text-[11px] leading-relaxed flex-1">{hint}</p>
        <span
          className={`text-[11px] font-mono shrink-0 ${
            nearLimit ? 'text-amber-500' : 'text-slate-300'
          }`}
        >
          {len}/{MAX}
        </span>
      </div>
    </div>
  );
}

export default function BusinessKnowledgeTab({ form, set }: Props) {
  return (
    <div className="space-y-5">

      <div className="flex items-start gap-3 px-4 py-3.5 bg-[#C8A96E]/8 rounded-xl border border-[#C8A96E]/25">
        <i className="ri-lightbulb-line text-[#C8A96E] text-sm mt-0.5 shrink-0" />
        <p className="text-[#0f172a]/65 text-xs leading-relaxed">
          The more context you provide, the more accurate and personalized the assistant&apos;s responses will
          be. Each section is sent as part of the system prompt — be specific and realistic.
        </p>
      </div>

      <ContextField
        label="About Your Business"
        icon="ri-building-line"
        fieldKey="business_context"
        value={form.business_context}
        placeholder="Describe your studio: what you photograph, your style, your ideal client, your location, awards, years of experience, what makes you unique…"
        hint="Core context. The AI uses this to understand who you are and match your brand voice."
        rows={5}
        onChange={(v) => set('business_context', v)}
      />

      <ContextField
        label="Services & Packages"
        icon="ri-stack-line"
        fieldKey="services_context"
        value={form.services_context}
        placeholder="List your main services and packages: Wedding Full Day — 10 hrs, 2 photographers, 400+ edited photos, online gallery. Engagement Session — 1.5 hrs, 50 photos…"
        hint="Helps the AI describe your offerings accurately when clients ask."
        rows={4}
        onChange={(v) => set('services_context', v)}
      />

      <ContextField
        label="Pricing"
        icon="ri-price-tag-3-line"
        fieldKey="pricing_context"
        value={form.pricing_context}
        placeholder="e.g. Wedding packages from $2,500 to $6,500. Custom quotes available. 30% deposit required at booking. Payment via bank transfer or card…"
        hint="Pricing context so the AI can answer 'how much does it cost?' type questions."
        rows={3}
        onChange={(v) => set('pricing_context', v)}
      />

      <ContextField
        label="Frequently Asked Questions"
        icon="ri-question-answer-line"
        fieldKey="faq_context"
        value={form.faq_context}
        placeholder="Q: How far in advance should I book? A: We recommend 6-12 months for weddings.\nQ: Do you travel? A: Yes, we cover all of [region] and travel nationally for an additional fee…"
        hint="Common questions and answers so the AI can respond instantly without guessing."
        rows={5}
        onChange={(v) => set('faq_context', v)}
      />

      {/* Completeness indicator */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-4">Knowledge Completeness</h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            {
              label: 'Business',
              value: form.business_context,
              minChars: 100,
              icon: 'ri-building-line',
            },
            {
              label: 'Services',
              value: form.services_context,
              minChars: 80,
              icon: 'ri-stack-line',
            },
            {
              label: 'Pricing',
              value: form.pricing_context,
              minChars: 50,
              icon: 'ri-price-tag-3-line',
            },
            {
              label: 'FAQ',
              value: form.faq_context,
              minChars: 80,
              icon: 'ri-question-answer-line',
            },
          ].map((item) => {
            const filled = item.value.length >= item.minChars;
            const partial = item.value.length > 0 && !filled;
            return (
              <div key={item.label} className="text-center">
                <div
                  className={`w-10 h-10 mx-auto flex items-center justify-center rounded-xl mb-2 ${
                    filled
                      ? 'bg-emerald-50 border border-emerald-200'
                      : partial
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-slate-50 border border-slate-200'
                  }`}
                >
                  <i
                    className={`${item.icon} text-sm ${
                      filled ? 'text-emerald-500' : partial ? 'text-amber-500' : 'text-slate-300'
                    }`}
                  />
                </div>
                <p
                  className={`text-[11px] font-medium ${
                    filled ? 'text-emerald-600' : partial ? 'text-amber-500' : 'text-slate-400'
                  }`}
                >
                  {item.label}
                </p>
                <p className="text-slate-300 text-[10px] mt-0.5">
                  {filled ? 'Good' : partial ? 'Partial' : 'Empty'}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}